/** Privacy video config for manna-dash. */

/**
 * Build tag for the privacy pipeline. Bump this whenever worker or processor
 * behaviour changes.
 *
 * The worker is a separately-cached script, so it can be stale while the main
 * bundle is fresh. The worker reports the value it was compiled with on
 * registration; if that differs from this constant, the browser is running a
 * cached worker. See `window.__mannaPrivacyStatus()`.
 */
export const PRIVACY_PIPELINE_BUILD = "pv-16-model-load-fail";

const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/$/, "");

/** stephan-best only (m-p2). No s-p2 / no model picker. */
export const STEPHAN_BEST_MODEL_PATH = `${publicUrl}/models/stephan/m-p2.onnx`;
export const STEPHAN_BEST_LABEL = "stephan-best";

/**
 * Env fallback when VideoQuic is mounted without blur/detect props.
 * Pilot dash ignores this and passes blur on / detect off.
 * Override with REACT_APP_PRIVACY_VIDEO=true for unscoped mounts.
 */
export const isPrivacyVideoEnabled = (): boolean =>
  process.env.REACT_APP_PRIVACY_VIDEO === "true";

/**
 * Compile-time kill-switch layered on person-detect. Must stay true so
 * /video-test can still enable boxes; the dash mounts detect off.
 */
export const PERSON_DETECTION_ENABLED = true;

/**
 * Blur radius for outside-ROI privacy, in **display** pixels. Tuned via
 * vid-blur.
 *
 * The blur worker draws at the JPEG's own resolution, so it scales this up by
 * the canvas downscale factor. Treating it as source pixels would make the
 * blur the operator sees get weaker as camera resolution rises.
 */
export const PRIVACY_BLUR_PX = 3;

/**
 * Bounds on the fitted cyan ROI ring radius as a fraction of
 * min(frameWidth, feedHeight) (status bar excluded). Used after a
 * centre-seeded radial peak is found — not as the primary score.
 * Rings smaller than the min (pad crosshair / reticle) do not lock.
 */
export const CIRCLE_ROI_MIN_RADIUS_RATIO = 0.12;
export const CIRCLE_ROI_MAX_RADIUS_RATIO = 0.55;

/**
 * Extra radius (px) added to the detected cyan ring so the sharp hole sits
 * outside the painted overlay rather than clipping it. Was 30.
 */
export const CIRCLE_RADIUS_PAD_PX = 40;

/** Soft feather around cyan ROI edge (destination-out). */
export const CIRCLE_FEATHER_PX = 6;

/**
 * Radius of the always-sharp centre region, as a fraction of the shorter feed
 * dimension (status bar excluded). Expressed as a ratio so it survives a camera
 * resolution change. Sized for a ~200 px disc at 720p (was ~150 px at 0.15).
 * ≈248 px across at 1280×720, ≈165 px at 640×480.
 */
export const CENTRE_CLEAR_RATIO = 0.2;

/**
 * Flat padding added to the centre clear radius, in source pixels. Widens the
 * guaranteed-sharp area under the aircraft without changing how it scales with
 * camera resolution. Matched to CIRCLE_RADIUS_PAD_PX.
 */
export const CENTRE_CLEAR_PAD_PX = 40;

/** How long an ROI lock is trusted before we re-scan for the ring. */
export const CIRCLE_LOCK_STALE_MS = 2000;

/**
 * Hard expiry for an ROI lock. Re-scans are allowed to miss transiently (motion
 * blur, compression) and keep the existing lock, but past this age an
 * unconfirmed lock is dropped rather than trusted indefinitely. Without this a
 * single early lock survives forever on feeds where the detector never finds a
 * ring again, leaving a sharp hole pinned to wherever it first matched.
 */
export const CIRCLE_LOCK_MAX_AGE_MS = 6000;

/**
 * CSS-blur fallback only if this stream's in-flight worker frame has not
 * returned. Waiting in the page-wide blur queue is not a stall — 10 feeds
 * serialised on 2 cores routinely sit idle >500 ms between completions.
 */
export const OVERLAY_STALL_MS = 2500;

/**
 * Floor between inferences. Detection is a page-wide budget, not per video:
 * ops machines are 2-core Windows boxes that also run telemetry sockets,
 * command HTTP, and the Datadog agent. Per-stream 1 Hz on 10 feeds would put
 * ~10 ONNX passes/sec on a core the main thread needs for commands.
 */
export const DETECT_INTERVAL_MS = 1000;

/** Extra spacing on machines with 2 or fewer logical cores. */
export const DETECT_LOW_CORE_INTERVAL_MS = 2500;

/**
 * Minimum time between any two inferences on the page. On a 2-core box this
 * is 2.5 s total, round-robin across feeds — not 1 Hz each.
 */
export const detectGlobalIntervalMs = (): number => {
  const cores =
    typeof navigator !== "undefined" ? navigator.hardwareConcurrency || 2 : 2;
  return cores <= 2 ? DETECT_LOW_CORE_INTERVAL_MS : DETECT_INTERVAL_MS;
};

/**
 * How long sticky person boxes remain useful between sparse detects.
 * Sized so a 10-feed round-robin at 2.5 s still holds boxes for a full cycle.
 */
export const DETECTION_HOLD_MS = 30_000;

/** Person detect confidence (stephan-best). */
export const DETECT_CONFIDENCE = 0.3;

/** ORT wasm assets served from public/ (offline-friendly). */
export const ORT_WASM_PATHS = `${publicUrl}/ort/`;
